import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../../../components/lab-dynamic-table";
import DeleteConfirmationModal from "../../../../components/Modals/delete-modal";

// Allowed abbreviations as per your requirement
const ALLOWED_ABBREVIATIONS = [
  "WB",
  "PRBC",
  "LD-PRBC",
  "W-PRBC",
  "FFP",
  "PLT",
  "SP",
  "FP",
  "CRYO-CP",
  "CRYO-SN",
  "BC",
  "MU",
  "PEX",
  "Other",
];

const BloodComponent: React.FC = () => {
  const [editData, setEditData] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string>("");
  const [dosageData, setDosageData] = useState<any[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    componentName: "",
    environment: "",
    expiryDays: 0,
    abbreviation: "", // Fixed spelling
  });

  const [abbreviationInput, setAbbreviationInput] = useState<string>("");

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const val = name === "expiryDays" ? Number(value) || 0 : value;
    setFormData((prev) => ({
      ...prev,
      [name]: val,
    }));
  };

  const fetchData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_BLOOD_BANK}/blood-component/getAllByPagination`,
        {
          params: {
            pageNo: page + 1,
            count: rowsPerPage,
            search: searchInput,
          },
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      setDosageData(response.data?.data || []);
      setTotal(response.data?.totalCount || 0);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load blood components");
    }
  };

  useEffect(() => {
    fetchData();
  }, [page, rowsPerPage, searchInput]);

  const handleSubmit = async () => {
    // Validate abbreviation
    if (!ALLOWED_ABBREVIATIONS.includes(formData.abbreviation)) {
      toast.error("Please select a valid abbreviation from the list.");
      return;
    }

    // Validate required fields
    if (
      !formData.componentName ||
      !formData.environment ||
      !formData.abbreviation ||
      formData.expiryDays <= 0
    ) {
      toast.error("Please fill all fields correctly.");
      return;
    }

    try {
      const url = editData
        ? `${process.env.REACT_APP_BLOOD_BANK}/blood-component/updateById/${editData}`
        : `${process.env.REACT_APP_BLOOD_BANK}/blood-component/addBloodComponent`;

      const method = editData ? axios.patch : axios.post;

      const response = await method(
        url,
        {
          componentName: formData.componentName,
          environment: formData.environment,
          expiryDays: formData.expiryDays,
          abbrivation: formData.abbreviation, // API still uses old spelling?
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data?.isSuccess) {
        toast.success(response.data.message);
        resetForm();
        fetchData();
      } else {
        toast.error(response.data?.message || "Operation failed");
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Something went wrong");
    }
  };

  const resetForm = () => {
    setFormData({
      componentName: "",
      environment: "",
      expiryDays: 0,
      abbreviation: "",
    });
    setAbbreviationInput("");
    setEditData(null);
  };

  const handleEditClick = (id: string, rowData: any) => {
    setEditData(id);
    setFormData({
      componentName: rowData.componentName || "",
      environment: rowData.environment || "",
      expiryDays: rowData.expiryDays || 0,
      abbreviation: rowData.abbrivation || "", // Match API field name
    });
    setAbbreviationInput(rowData.abbrivation || "");
  };

  const handleDeleteClick = (id: string) => {
    setSelectedItemId(id);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_BLOOD_BANK}/blood-component/deleteById/${selectedItemId}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );
      toast.success("Blood component deleted successfully");
      fetchData();
      resetForm();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Delete failed");
    }
    setIsDeleteModalOpen(false);
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  return (
    <Box>
      <Box
        sx={{
          background: "#f4f4f4",
          borderRadius: 2,
          p: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h5" fontWeight="bold">
          Blood Components
        </Typography>

        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            placeholder="Search component..."
            value={searchInput}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchInput(e.target.value)
            }
            InputProps={{
              endAdornment: (
                <IconButton>
                  <SearchIcon sx={{ color: "#055FFC" }} />
                </IconButton>
              ),
            }}
          />
        </Box>
      </Box>

      <Grid container spacing={3}>
        <Grid item xs={12} md={4}>
          <Box sx={{ backgroundColor: "white", borderRadius: 2, p: 3, boxShadow: 1 }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {editData ? "Update Blood Component" : "Add New Blood Component"}
            </Typography>

            <TextField
              fullWidth
              size="small"
              label="Component Name"
              name="componentName"
              className="custom-input"
              value={formData.componentName}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Storage Environment"
              name="environment"
              className="custom-input"
              value={formData.environment}
              onChange={handleChange}
              sx={{ mb: 2 }}
            />

            <TextField
              fullWidth
              size="small"
              label="Expiry Days"
              name="expiryDays"
              type="number"
               className="custom-input"
              value={formData.expiryDays}
              onChange={handleChange}
              InputProps={{ inputProps: { min: 1 } }}
              sx={{ mb: 2 }}
            />

            <Autocomplete
              options={ALLOWED_ABBREVIATIONS}
              value={formData.abbreviation}
              onChange={(_event, newValue) => {
                setFormData((prev) => ({ ...prev, abbreviation: newValue || "" }));
              }}
              inputValue={abbreviationInput}
              onInputChange={(_event, newInputValue) => {
                setAbbreviationInput(newInputValue);
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                   className="custom-input"
                  label="Abbreviation"
                  size="small"
                  helperText={
                    formData.abbreviation &&
                    !ALLOWED_ABBREVIATIONS.includes(formData.abbreviation)
                      ? "Invalid abbreviation"
                      : ""
                  }
                  sx={{ mb: 2 }}
                />
              )}
            />

            <Box display="flex" gap={2}>
              <Button
                fullWidth
                variant="contained"
                color="primary"
                onClick={handleSubmit}
              >
                {editData ? "Update" : "Save"}
              </Button>

              {editData && (
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={resetForm}
                >
                  Cancel
                </Button>
              )}
            </Box>
          </Box>
        </Grid>

        <Grid item xs={12} md={8}>
          <DynamicTable
            headers={[
              "SN",
              "Component Name",
              "Abbreviation",
              "Environment",
              "Expiry Days",
              "Actions",
            ]}
            data={dosageData.map((item: any, index: number) => [
              page * rowsPerPage + index + 1,
              item.componentName,
              item.abbrivation,
              item.environment,
              `${item.expiryDays} days`,
            ])}
            loading={false}
            page={page}
            rowsPerPage={rowsPerPage}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            totalRows={total}
            actionButtons={[
              {
                icon: <EditIcon sx={{ color: "#FFB800" }} />,
                onClick: (rowIndex: number) =>
                  handleEditClick(dosageData[rowIndex]._id, dosageData[rowIndex]),
              },
              {
                icon: <DeleteIcon sx={{ color: "#FF5454" }} />,
                onClick: (rowIndex: number) =>
                  handleDeleteClick(dosageData[rowIndex]._id),
              },
            ]}
          />

          {dosageData.length === 0 && (
            <Typography
              variant="h6"
              color="textSecondary"
              align="center"
              sx={{ mt: 4 }}
            >
              No blood components found
            </Typography>
          )}
        </Grid>
      </Grid>

      <DeleteConfirmationModal
        open={isDeleteModalOpen}
        handleClose={() => setIsDeleteModalOpen(false)}
        handleConfirm={handleDeleteConfirm}
        message="Are you sure you want to delete this blood component? This action cannot be undone."
      />
    </Box>
  );
};

export default BloodComponent;